import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Search, Edit2, Trash2, Key, Mail, Phone, MapPin,
  Loader2, Check, X, Eye, BarChart3, Calendar, Clock, 
  CheckCircle2, XCircle, AlertCircle, ExternalLink, Globe,
  Instagram, Twitter, Youtube, Music, DollarSign, Ticket, Users, ShieldCheck,
  Crown, Settings, CreditCard, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', icon: Clock, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
  approved: { label: 'Aprobado', icon: CheckCircle2, color: 'text-green-400 bg-green-500/10 border-green-500/30' },
  rejected: { label: 'Rechazado', icon: XCircle, color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  contacted: { label: 'Contactado', icon: Mail, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
};

const TABS = [
  { id: 'producers', label: 'Productoras Activas', icon: Building2 },
  { id: 'applications', label: 'Solicitudes', icon: AlertCircle },
  { id: 'subscriptions', label: 'Super Productora', icon: Crown },
];

const ProducersAdminPage = () => {
  const navigate = useNavigate();
  const [producers, setProducers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProducer, setEditingProducer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [selectedProducer, setSelectedProducer] = useState(null);
  
  const [editFormData, setEditFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    province: '',
    city: '',
    description: '',
    slug: '',
    service_fee_percent: 10,
    is_verified: false,
    is_active: true,
    bank_info: {
      bank_name: '',
      account_holder: '',
      cbu: '',
      alias: '',
      cuit: ''
    }
  });
  
  const [approvalData, setApprovalData] = useState({
    slug: '',
    service_fee_percent: 10,
    is_verified: false
  });

  const [createFormData, setCreateFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    province: '',
    city: '',
    description: '',
    service_fee_percent: 10,
    is_verified: false
  });

  // Super Productora subscription states
  const [subscriptionSettings, setSubscriptionSettings] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showGrantSubModal, setShowGrantSubModal] = useState(false);
  const [grantSubProducer, setGrantSubProducer] = useState(null);
  const [grantSubData, setGrantSubData] = useState({
    expires_at: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptionData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [producersRes, appsRes] = await Promise.all([
        api.get('/producers/admin/all').catch((err) => {
          if (err.response?.status === 401) throw err;
          return { data: [] };
        }),
        api.get('/producers/admin/applications').catch((err) => {
          if (err.response?.status === 401) throw err;
          return { data: [] };
        })
      ]);
      setProducers(producersRes.data || []);
      setApplications(appsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada. Redirigiendo al login...');
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/admin/login');
        }, 1500);
        return;
      }
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionData = async () => {
    setLoadingSubscriptions(true);
    try {
      const [settingsRes, subsRes] = await Promise.all([
        api.get('/producers/admin/subscription/settings'),
        api.get('/producers/admin/subscriptions')
      ]);
      setSubscriptionSettings(settingsRes.data);
      setSubscriptions(subsRes.data || []);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, vuelve a iniciar sesión.');
      } else {
        toast.error('Error al cargar datos de suscripciones');
      }
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleSaveSubscriptionSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/producers/admin/subscription/settings', subscriptionSettings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGrantSubscription = async () => {
    if (!grantSubProducer) return;
    
    setSavingSettings(true);
    try {
      await api.put(`/producers/admin/${grantSubProducer.id}/subscription`, {
        status: 'active',
        plan_type: 'monthly',
        is_manual: true,
        expires_at: grantSubData.expires_at || null,
        notes: grantSubData.notes || null
      });
      toast.success(`Suscripción otorgada a ${grantSubProducer.business_name}`);
      setShowGrantSubModal(false);
      setGrantSubProducer(null);
      setGrantSubData({ expires_at: '', notes: '' });
      fetchSubscriptionData();
    } catch (error) {
      toast.error('Error al otorgar suscripción');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCancelSubscription = async (producerId, businessName) => {
    if (!confirm(`¿Cancelar suscripción de ${businessName}?`)) return;
    
    try {
      await api.delete(`/producers/admin/${producerId}/subscription`);
      toast.success('Suscripción cancelada');
      fetchSubscriptionData();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleApprove = async () => {
    if (!selectedApp || !approvalData.slug) return;
    
    setApproving(true);
    try {
      const response = await api.post(`/producers/admin/applications/${selectedApp.id}/approve`, approvalData);
      
      // Show the temporary password
      if (response.data?.temp_password) {
        toast.success(
          <div className="space-y-2">
            <p className="font-bold">Productora aprobada</p>
            <p className="text-sm">Contraseña temporal: <span className="font-mono bg-zinc-800 px-2 py-1 rounded">{response.data.temp_password}</span></p>
            <p className="text-xs text-zinc-400">Email: {selectedApp.email}</p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success('Productora aprobada correctamente');
      }
      
      setShowApproveModal(false);
      setSelectedApp(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    
    setApproving(true);
    try {
      await api.post(`/producers/admin/applications/${selectedApp.id}/reject`, {
        rejection_reason: rejectionReason
      });
      toast.success('Solicitud rechazada');
      setShowRejectModal(false);
      setSelectedApp(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally {
      setApproving(false);
    }
  };

  const handleToggleActive = async (producerId) => {
    try {
      await api.post(`/producers/admin/${producerId}/toggle-active`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleToggleVerified = async (producerId) => {
    try {
      const response = await api.post(`/producers/admin/${producerId}/toggle-verified`);
      toast.success(response.data.is_verified ? 'Productora verificada' : 'Verificación removida');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar verificación');
    }
  };

  const handleResetPassword = async (producer) => {
    try {
      const response = await api.post(`/producers/admin/${producer.id}/reset-password`);
      setNewPassword(response.data.new_password);
      setSelectedProducer(producer);
      setShowPasswordModal(true);
    } catch (error) {
      toast.error('Error al resetear contraseña');
    }
  };

  const handleViewProducerDetails = async (producerId) => {
    try {
      const response = await api.get(`/producers/admin/${producerId}/details`);
      setSelectedProducer(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleCreateProducer = async () => {
    if (!createFormData.business_name || !createFormData.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    
    setApproving(true);
    try {
      const response = await api.post('/producers/admin/create', createFormData);
      setNewPassword(response.data.temp_password);
      setSelectedProducer({ business_name: createFormData.business_name, email: createFormData.email });
      setShowCreateModal(false);
      setShowPasswordModal(true);
      setCreateFormData({
        business_name: '',
        contact_name: '',
        email: '',
        phone: '',
        province: '',
        city: '',
        description: '',
        service_fee_percent: 10,
        is_verified: false
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear productora');
    } finally {
      setApproving(false);
    }
  };

  const openApproveModal = (app) => {
    setSelectedApp(app);
    setApprovalData({
      slug: generateSlug(app.business_name),
      service_fee_percent: 10,
      is_verified: false
    });
    setShowApproveModal(true);
  };

  const openRejectModal = (app) => {
    setSelectedApp(app);
    setShowRejectModal(true);
  };

  const openEditModal = (producer) => {
    setEditingProducer(producer);
    setEditFormData({
      business_name: producer.business_name || '',
      contact_name: producer.contact_name || '',
      email: producer.email || '',
      phone: producer.phone || '',
      province: producer.province || '',
      city: producer.city || '',
      description: producer.description || '',
      slug: producer.slug || '',
      service_fee_percent: producer.service_fee_percent || 10,
      is_verified: producer.is_verified || false,
      is_active: producer.is_active !== false,
      bank_info: {
        bank_name: producer.bank_info?.bank_name || '',
        account_holder: producer.bank_info?.account_holder || '',
        cbu: producer.bank_info?.cbu || '',
        alias: producer.bank_info?.alias || '',
        cuit: producer.bank_info?.cuit || ''
      }
    });
    setShowEditModal(true);
  };

  const handleSaveProducer = async () => {
    if (!editingProducer) return;
    
    setSaving(true);
    try {
      await api.put(`/producers/admin/${editingProducer.id}`, editFormData);
      toast.success('Productora actualizada correctamente');
      setShowEditModal(false);
      setEditingProducer(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar productora');
    } finally {
      setSaving(false);
    }
  };

  const filteredProducers = producers.filter(p => 
    p.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredApplications = applications.filter(a => 
    a.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="producers-admin-page">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black text-white flex items-center gap-3">
                <Building2 className="text-[#D4AF37]" />
                Gestión de Productoras
              </h1>
              <p className="text-zinc-400 mt-1">Administra las productoras y sus solicitudes</p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
            >
              <Users size={16} className="mr-2" />
              Nueva Productora
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="text-[#D4AF37]" size={20} />
                <span className="text-zinc-400 text-sm">Productoras</span>
              </div>
              <p className="text-2xl font-black text-white">{producers.length}</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="text-yellow-500" size={20} />
                <span className="text-zinc-400 text-sm">Pendientes</span>
              </div>
              <p className="text-2xl font-black text-yellow-400">{pendingCount}</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="text-green-500" size={20} />
                <span className="text-zinc-400 text-sm">Verificadas</span>
              </div>
              <p className="text-2xl font-black text-green-400">
                {producers.filter(p => p.is_verified).length}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <Ticket className="text-purple-500" size={20} />
                <span className="text-zinc-400 text-sm">Total Eventos</span>
              </div>
              <p className="text-2xl font-black text-purple-400">
                {producers.reduce((sum, p) => sum + (p.total_events || 0), 0)}
              </p>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-zinc-800 mb-6">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    isActive 
                      ? 'border-[#D4AF37] text-[#D4AF37]' 
                      : 'border-transparent text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                  {tab.id === 'applications' && pendingCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
            </div>
          ) : activeTab === 'applications' ? (
            /* Applications Tab */
            <div className="space-y-4">
              {filteredApplications.length === 0 ? (
                <div className="text-center py-16">
                  <AlertCircle size={48} className="mx-auto text-zinc-700 mb-4" />
                  <p className="text-zinc-500">No hay solicitudes</p>
                </div>
              ) : (
                filteredApplications.map((app, index) => {
                  const status = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                  const StatusIcon = status.icon;
                  return (
                    <motion.div
                      key={app.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
                      data-testid={`application-${app.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-white">{app.business_name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${status.color}`}>
                              <StatusIcon size={12} />
                              {status.label}
                            </span>
                          </div>
                          
                          <p className="text-zinc-400 text-sm mb-4">{app.description}</p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Mail size={14} />
                              <span className="truncate">{app.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Phone size={14} />
                              <span>{app.phone}</span>
                            </div>
                            <div className="flex items-center gap-2 text-zinc-400">
                              <MapPin size={14} />
                              <span>{app.city}, {app.province}</span>
                            </div>
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Calendar size={14} />
                              <span>{new Date(app.created_at).toLocaleDateString('es-AR')}</span>
                            </div>
                          </div>
                          
                          {/* Additional Info */}
                          <div className="mt-4 flex flex-wrap gap-4 text-sm">
                            <span className="text-zinc-500">
                              Eventos/mes: <span className="text-white">{app.expected_events_per_month}</span>
                            </span>
                            <span className="text-zinc-500">
                              Capacidad: <span className="text-white">{app.average_event_capacity}</span>
                            </span>
                            {app.website && (
                              <a 
                                href={app.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-[#D4AF37] hover:underline"
                              >
                                <Globe size={14} />
                                Sitio web
                              </a>
                            )}
                          </div>

                          {/* Social Links */}
                          {app.social_links?.length > 0 && (
                            <div className="mt-3 flex gap-2">
                              {app.social_links.map((link, i) => (
                                <a 
                                  key={i}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
                                >
                                  {link.platform === 'instagram' && <Instagram size={16} />}
                                  {link.platform === 'twitter' && <Twitter size={16} />}
                                  {link.platform === 'youtube' && <Youtube size={16} />}
                                  {link.platform === 'spotify' && <Music size={16} />}
                                </a>
                              ))}
                            </div>
                          )}

                          {app.message && (
                            <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                              <p className="text-zinc-400 text-sm italic">"{app.message}"</p>
                            </div>
                          )}
                          
                          {/* Form Responses Indicator */}
                          {app.form_responses?.length > 0 && (
                            <p className="mt-2 text-xs text-[#D4AF37]">
                              {app.form_responses.length} campos adicionales en el formulario
                            </p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 lg:flex-col">
                          <Button
                            onClick={() => {
                              setSelectedApp(app);
                              setShowApplicationDetail(true);
                            }}
                            variant="outline"
                            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`view-detail-${app.id}`}
                          >
                            <Eye size={16} className="mr-2" />
                            Ver Detalle
                          </Button>
                          {app.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => openApproveModal(app)}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              >
                                <Check size={16} className="mr-2" />
                                Aprobar
                              </Button>
                              <Button
                                onClick={() => openRejectModal(app)}
                                variant="outline"
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <X size={16} className="mr-2" />
                                Rechazar
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'producers' ? (
            /* Producers Tab */
            <div className="space-y-4">
              {filteredProducers.length === 0 ? (
                <div className="text-center py-16">
                  <Building2 size={48} className="mx-auto text-zinc-700 mb-4" />
                  <p className="text-zinc-500">No hay productoras registradas</p>
                </div>
              ) : (
                filteredProducers.map((producer, index) => (
                  <motion.div
                    key={producer.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
                    data-testid={`producer-${producer.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Logo */}
                      {producer.branding?.logo_url ? (
                        <img 
                          src={producer.branding.logo_url} 
                          alt={producer.business_name}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center">
                          <Building2 className="text-[#D4AF37]" size={28} />
                        </div>
                      )}
                      
                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-bold text-white">{producer.business_name}</h3>
                          {producer.is_verified && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                              VERIFICADO
                            </span>
                          )}
                          {!producer.is_active && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                              INACTIVO
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {producer.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {producer.city}, {producer.country}
                          </span>
                        </div>
                        
                        {/* Stats */}
                        <div className="flex gap-6 mt-3 text-sm">
                          <span className="text-zinc-500">
                            <Ticket size={14} className="inline mr-1" />
                            Eventos: <span className="text-white">{producer.total_events || 0}</span>
                          </span>
                          <span className="text-zinc-500">
                            <Users size={14} className="inline mr-1" />
                            Comunidad: <span className="text-white">{producer.community_size || 0}</span>
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewProducerDetails(producer.id)}
                          className="text-zinc-400 hover:text-white"
                          title="Ver detalles"
                          data-testid={`view-producer-details-${producer.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(producer)}
                          className="text-zinc-400 hover:text-[#D4AF37]"
                          title="Editar productora"
                          data-testid={`edit-producer-${producer.id}`}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleVerified(producer.id)}
                          className={producer.is_verified ? 'text-green-400 hover:text-zinc-400' : 'text-zinc-400 hover:text-green-400'}
                          title={producer.is_verified ? 'Quitar verificación' : 'Verificar productora'}
                        >
                          <ShieldCheck size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(producer)}
                          className="text-zinc-400 hover:text-[#D4AF37]"
                          title="Cambiar contraseña"
                        >
                          <Key size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(producer.id)}
                          className={producer.is_active !== false ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'}
                          title={producer.is_active !== false ? 'Desactivar' : 'Activar'}
                        >
                          {producer.is_active !== false ? <Check size={16} /> : <X size={16} />}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          ) : activeTab === 'subscriptions' ? (
            /* Super Productora Subscriptions Tab */
            <div className="space-y-6">
              {loadingSubscriptions ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                </div>
              ) : (
                <>
                  {/* Settings Card */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-amber-600 rounded-xl flex items-center justify-center">
                          <Crown className="text-black" size={24} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Configuración Super Productora</h3>
                          <p className="text-sm text-zinc-500">Gestiona precios, beneficios y link de pago</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#D4AF37]">{subscriptionSettings?.stats?.total_premium || 0}</p>
                          <p className="text-zinc-500">Premium</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-zinc-400">{subscriptionSettings?.stats?.total_free || 0}</p>
                          <p className="text-zinc-500">Gratuitas</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Pricing */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-white flex items-center gap-2">
                          <DollarSign size={16} className="text-[#D4AF37]" />
                          Precios
                        </h4>
                        <div>
                          <Label className="text-zinc-400">Precio Mensual ({subscriptionSettings?.currency || 'ARS'})</Label>
                          <Input
                            type="number"
                            value={subscriptionSettings?.price_monthly || 15000}
                            onChange={(e) => setSubscriptionSettings({
                              ...subscriptionSettings,
                              price_monthly: parseFloat(e.target.value) || 0
                            })}
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={subscriptionSettings?.annual_plan_enabled || false}
                            onCheckedChange={(checked) => setSubscriptionSettings({
                              ...subscriptionSettings,
                              annual_plan_enabled: checked
                            })}
                          />
                          <Label className="text-zinc-400">Habilitar plan anual</Label>
                        </div>
                        {subscriptionSettings?.annual_plan_enabled && (
                          <div>
                            <Label className="text-zinc-400">Precio Anual ({subscriptionSettings?.currency || 'ARS'})</Label>
                            <Input
                              type="number"
                              value={subscriptionSettings?.price_annual || ''}
                              onChange={(e) => setSubscriptionSettings({
                                ...subscriptionSettings,
                                price_annual: parseFloat(e.target.value) || null
                              })}
                              placeholder="Ej: 150000 (con descuento)"
                              className="bg-zinc-800 border-zinc-700 mt-1"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Rebill Link */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-white flex items-center gap-2">
                          <Link2 size={16} className="text-[#D4AF37]" />
                          Link de Pago Rebill
                        </h4>
                        <div>
                          <Label className="text-zinc-400">Link de Suscripción Mensual</Label>
                          <Input
                            value={subscriptionSettings?.rebill_link || ''}
                            onChange={(e) => setSubscriptionSettings({
                              ...subscriptionSettings,
                              rebill_link: e.target.value
                            })}
                            placeholder="https://pay.rebill.com/..."
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        {subscriptionSettings?.annual_plan_enabled && (
                          <div>
                            <Label className="text-zinc-400">Link de Suscripción Anual</Label>
                            <Input
                              value={subscriptionSettings?.rebill_link_annual || ''}
                              onChange={(e) => setSubscriptionSettings({
                                ...subscriptionSettings,
                                rebill_link_annual: e.target.value
                              })}
                              placeholder="https://pay.rebill.com/..."
                              className="bg-zinc-800 border-zinc-700 mt-1"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Benefits Summary */}
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <h4 className="font-medium text-white mb-4 flex items-center gap-2">
                        <Settings size={16} className="text-[#D4AF37]" />
                        Beneficios del Plan Premium
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Venues</p>
                          <p className="text-white font-medium">Hasta {subscriptionSettings?.premium_limits?.max_venues || 5}</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Estilos</p>
                          <p className="text-white font-medium">{subscriptionSettings?.premium_limits?.allowed_styles?.length || 9} estilos</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Empleos</p>
                          <p className="text-white font-medium">Ilimitados</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Emails/mes</p>
                          <p className="text-white font-medium">{subscriptionSettings?.premium_limits?.monthly_emails || 1000}</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Comisión</p>
                          <p className="text-green-400 font-medium">{subscriptionSettings?.premium_limits?.service_fee_percent || 10}%</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                          <p className="text-zinc-500">Liquidación anticipada</p>
                          <p className="text-white font-medium">Hasta {subscriptionSettings?.premium_limits?.early_settlement_max_percent || 50}%</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={handleSaveSubscriptionSettings}
                        disabled={savingSettings}
                        className="bg-[#D4AF37] hover:bg-[#C4A030] text-black"
                      >
                        {savingSettings ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Guardar Configuración
                      </Button>
                    </div>
                  </div>
                  
                  {/* Subscriptions List */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-white">Productoras con Suscripción</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Show list of producers to grant subscription
                          const producer = producers.find(p => !subscriptions.find(s => s.producer_id === p.id && s.status === 'active'));
                          if (producer) {
                            setGrantSubProducer(producer);
                            setShowGrantSubModal(true);
                          } else {
                            toast.info('Todas las productoras ya tienen suscripción activa');
                          }
                        }}
                        className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      >
                        <Crown size={16} className="mr-2" />
                        Otorgar Suscripción
                      </Button>
                    </div>
                    
                    {subscriptions.length === 0 ? (
                      <div className="text-center py-12">
                        <Crown size={48} className="mx-auto text-zinc-700 mb-4" />
                        <p className="text-zinc-500">No hay suscripciones activas</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {subscriptions.map(sub => (
                          <div 
                            key={sub.id}
                            className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                sub.status === 'active' ? 'bg-[#D4AF37]/20' : 'bg-zinc-700/50'
                              }`}>
                                <Crown className={sub.status === 'active' ? 'text-[#D4AF37]' : 'text-zinc-500'} size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-white">{sub.producer?.business_name || 'Productora'}</p>
                                <p className="text-sm text-zinc-500">{sub.producer?.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right text-sm">
                                <p className={`font-medium ${
                                  sub.status === 'active' ? 'text-green-400' : 
                                  sub.status === 'cancelled' ? 'text-red-400' : 'text-zinc-400'
                                }`}>
                                  {sub.status === 'active' ? 'Activa' : 
                                   sub.status === 'cancelled' ? 'Cancelada' : 'Expirada'}
                                </p>
                                {sub.expires_at && (
                                  <p className="text-zinc-500">
                                    {new Date(sub.expires_at) > new Date() ? 'Expira' : 'Expiró'}: {new Date(sub.expires_at).toLocaleDateString('es-AR')}
                                  </p>
                                )}
                                {sub.is_manual && (
                                  <span className="text-xs text-amber-500">Manual</span>
                                )}
                              </div>
                              {sub.status === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelSubscription(sub.producer_id, sub.producer?.business_name)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <X size={16} />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Quick Grant to any producer */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Otorgar Suscripción Manual</h3>
                    <p className="text-sm text-zinc-500 mb-4">
                      Selecciona una productora para otorgarle suscripción Super Productora de forma manual
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                      {producers
                        .filter(p => !subscriptions.find(s => s.producer_id === p.id && s.status === 'active'))
                        .map(producer => (
                          <button
                            key={producer.id}
                            onClick={() => {
                              setGrantSubProducer(producer);
                              setShowGrantSubModal(true);
                            }}
                            className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                          >
                            <Building2 size={16} className="text-zinc-500" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{producer.business_name}</p>
                              <p className="text-xs text-zinc-500">Plan Gratuito</p>
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* Approve Modal */}
        <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Aprobar Productora</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Slug (URL)</Label>
                <Input
                  value={approvalData.slug}
                  onChange={(e) => setApprovalData({ ...approvalData, slug: e.target.value })}
                  placeholder="mi-productora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  URL: /productora/{approvalData.slug}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_verified"
                  checked={approvalData.is_verified}
                  onChange={(e) => setApprovalData({ ...approvalData, is_verified: e.target.checked })}
                  className="rounded border-zinc-700"
                />
                <Label htmlFor="is_verified" className="text-zinc-400">
                  Marcar como verificada (puede publicar eventos directamente)
                </Label>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowApproveModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApprove}
                disabled={approving || !approvalData.slug}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              >
                {approving ? <Loader2 className="animate-spin" /> : 'Aprobar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Rechazar Solicitud</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <Label className="text-zinc-400">Motivo del rechazo</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explica el motivo del rechazo..."
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={4}
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleReject}
                disabled={approving}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {approving ? <Loader2 className="animate-spin" /> : 'Rechazar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Producer Detail Modal */}
        <Dialog open={!!selectedProducer} onOpenChange={() => setSelectedProducer(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                {selectedProducer?.branding?.logo_url ? (
                  <img 
                    src={selectedProducer.branding.logo_url} 
                    alt="" 
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                    <Building2 className="text-[#D4AF37]" size={20} />
                  </div>
                )}
                {selectedProducer?.business_name}
                {selectedProducer?.is_verified && (
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                    VERIFICADO
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedProducer && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Email</span>
                    <p className="text-white">{selectedProducer.email}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Contacto</span>
                    <p className="text-white">{selectedProducer.contact_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Teléfono</span>
                    <p className="text-white">{selectedProducer.phone || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Ubicación</span>
                    <p className="text-white">{[selectedProducer.city, selectedProducer.province, selectedProducer.country].filter(Boolean).join(', ') || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Slug</span>
                    <p className="text-[#D4AF37]">/{selectedProducer.slug}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Costo Servicio</span>
                    <p className="text-white">{selectedProducer.service_fee_percent || 10}%</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Estado</span>
                    <p className={selectedProducer.is_active !== false ? 'text-green-400' : 'text-red-400'}>
                      {selectedProducer.is_active !== false ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Total Eventos</span>
                    <p className="text-white">{selectedProducer.total_events || 0}</p>
                  </div>
                </div>
                
                {selectedProducer.description && (
                  <div>
                    <span className="text-zinc-500 text-sm">Descripción</span>
                    <p className="text-white text-sm mt-1">{selectedProducer.description}</p>
                  </div>
                )}
                
                {selectedProducer.social_links?.length > 0 && (
                  <div>
                    <span className="text-zinc-500 text-sm">Redes Sociales</span>
                    <div className="flex gap-2 mt-2">
                      {selectedProducer.social_links.map((link, i) => (
                        <a 
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
                        >
                          {link.platform === 'instagram' && <Instagram size={18} />}
                          {link.platform === 'twitter' && <Twitter size={18} />}
                          {link.platform === 'youtube' && <Youtube size={18} />}
                          {link.platform === 'spotify' && <Music size={18} />}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bank Info Section */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <DollarSign size={16} />
                    Datos Bancarios
                  </h4>
                  {selectedProducer.bank_info ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Banco</span>
                        <p className="text-white">{selectedProducer.bank_info.bank_name || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Titular</span>
                        <p className="text-white">{selectedProducer.bank_info.account_holder || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">CBU</span>
                        <p className="text-white font-mono text-xs">{selectedProducer.bank_info.cbu || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Alias</span>
                        <p className="text-white">{selectedProducer.bank_info.alias || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">CUIT</span>
                        <p className="text-white">{selectedProducer.bank_info.cuit || '-'}</p>
                      </div>
                      {selectedProducer.bank_info.is_locked && (
                        <div>
                          <span className="text-zinc-500">Estado</span>
                          <p className="text-yellow-400 text-xs">Datos bloqueados</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No hay datos bancarios registrados</p>
                  )}
                </div>

                {/* Branding Preview */}
                {selectedProducer.branding && (
                  <div className="p-4 bg-zinc-800/50 rounded-xl">
                    <span className="text-zinc-500 text-sm">Personalización de Marca</span>
                    <div className="flex items-center gap-4 mt-2">
                      {selectedProducer.branding.primary_color && (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded-full border border-zinc-600"
                            style={{ backgroundColor: selectedProducer.branding.primary_color }}
                          />
                          <span className="text-xs text-zinc-400">Primario</span>
                        </div>
                      )}
                      {selectedProducer.branding.secondary_color && (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded-full border border-zinc-600"
                            style={{ backgroundColor: selectedProducer.branding.secondary_color }}
                          />
                          <span className="text-xs text-zinc-400">Secundario</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Producer Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Nueva Productora</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-zinc-400">Nombre de la productora *</Label>
                  <Input
                    value={createFormData.business_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, business_name: e.target.value })}
                    placeholder="Mi Productora"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Nombre de contacto</Label>
                  <Input
                    value={createFormData.contact_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, contact_name: e.target.value })}
                    placeholder="Juan Pérez"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                    placeholder="contacto@productora.com"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Teléfono</Label>
                  <Input
                    value={createFormData.phone}
                    onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                    placeholder="+54 11 1234-5678"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Provincia</Label>
                  <Input
                    value={createFormData.province}
                    onChange={(e) => setCreateFormData({ ...createFormData, province: e.target.value })}
                    placeholder="Buenos Aires"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Ciudad</Label>
                  <Input
                    value={createFormData.city}
                    onChange={(e) => setCreateFormData({ ...createFormData, city: e.target.value })}
                    placeholder="CABA"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-zinc-400">Descripción</Label>
                  <Textarea
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                    placeholder="Breve descripción de la productora..."
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="create_is_verified"
                    checked={createFormData.is_verified}
                    onChange={(e) => setCreateFormData({ ...createFormData, is_verified: e.target.checked })}
                    className="rounded border-zinc-700"
                  />
                  <Label htmlFor="create_is_verified" className="text-zinc-400">
                    Marcar como verificada (puede publicar eventos directamente)
                  </Label>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateProducer}
                disabled={approving || !createFormData.business_name || !createFormData.email}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-black"
              >
                {approving ? <Loader2 className="animate-spin" /> : 'Crear Productora'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Key className="text-[#D4AF37]" size={20} />
                Credenciales de Acceso
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-6 space-y-4">
              <div className="p-4 bg-zinc-800 rounded-xl">
                <p className="text-zinc-400 text-sm mb-1">Productora</p>
                <p className="text-white font-bold">{selectedProducer?.business_name}</p>
              </div>
              
              <div className="p-4 bg-zinc-800 rounded-xl">
                <p className="text-zinc-400 text-sm mb-1">Email</p>
                <p className="text-white font-mono">{selectedProducer?.email}</p>
              </div>
              
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <p className="text-green-400 text-sm mb-1">Nueva Contraseña</p>
                <p className="text-white font-mono text-lg font-bold">{newPassword}</p>
              </div>
              
              <p className="text-zinc-500 text-xs text-center">
                Guarda esta contraseña. No se mostrará de nuevo.
              </p>
            </div>
            
            <Button
              onClick={() => {
                navigator.clipboard.writeText(newPassword);
                toast.success('Contraseña copiada al portapapeles');
              }}
              className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            >
              Copiar Contraseña
            </Button>
          </DialogContent>
        </Dialog>

        {/* Edit Producer Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit2 className="text-[#D4AF37]" size={20} />
                Editar Productora
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Información Básica</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Nombre de la Productora *</Label>
                    <Input
                      value={editFormData.business_name}
                      onChange={(e) => setEditFormData({...editFormData, business_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Slug (URL)</Label>
                    <Input
                      value={editFormData.slug}
                      onChange={(e) => setEditFormData({...editFormData, slug: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="mi-productora"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Email *</Label>
                    <Input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Teléfono</Label>
                    <Input
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Contacto</Label>
                    <Input
                      value={editFormData.contact_name}
                      onChange={(e) => setEditFormData({...editFormData, contact_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Costo de Servicio (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editFormData.service_fee_percent}
                      onChange={(e) => setEditFormData({...editFormData, service_fee_percent: parseInt(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Provincia</Label>
                    <Input
                      value={editFormData.province}
                      onChange={(e) => setEditFormData({...editFormData, province: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Ciudad</Label>
                    <Input
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400">Descripción</Label>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[80px]"
                  />
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.is_verified}
                      onChange={(e) => setEditFormData({...editFormData, is_verified: e.target.checked})}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-zinc-300 text-sm">Verificada</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500 focus:ring-green-500"
                    />
                    <span className="text-zinc-300 text-sm">Activa</span>
                  </label>
                </div>
              </div>

              {/* Bank Info Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                  <DollarSign size={16} />
                  Datos Bancarios
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Banco</Label>
                    <Input
                      value={editFormData.bank_info.bank_name}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        bank_info: {...editFormData.bank_info, bank_name: e.target.value}
                      })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Titular</Label>
                    <Input
                      value={editFormData.bank_info.account_holder}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        bank_info: {...editFormData.bank_info, account_holder: e.target.value}
                      })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">CBU</Label>
                    <Input
                      value={editFormData.bank_info.cbu}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        bank_info: {...editFormData.bank_info, cbu: e.target.value}
                      })}
                      className="bg-zinc-800 border-zinc-700 text-white font-mono mt-1"
                      placeholder="0000000000000000000000"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Alias</Label>
                    <Input
                      value={editFormData.bank_info.alias}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        bank_info: {...editFormData.bank_info, alias: e.target.value}
                      })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">CUIT</Label>
                    <Input
                      value={editFormData.bank_info.cuit}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        bank_info: {...editFormData.bank_info, cuit: e.target.value}
                      })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="XX-XXXXXXXX-X"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-zinc-800">
              <Button
                variant="ghost"
                onClick={() => setShowEditModal(false)}
                className="text-zinc-400"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveProducer}
                disabled={saving || !editFormData.business_name || !editFormData.email}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Guardar Cambios
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Application Detail Modal */}
        <Dialog open={showApplicationDetail} onOpenChange={setShowApplicationDetail}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Building2 className="text-[#D4AF37]" size={20} />
                </div>
                Detalle de Solicitud
              </DialogTitle>
            </DialogHeader>
            
            {selectedApp && (
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <h3 className="text-lg font-bold text-white mb-3">{selectedApp.business_name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Mail size={14} className="text-[#D4AF37]" />
                      <span>{selectedApp.email || 'No proporcionado'}</span>
                    </div>
                    {selectedApp.phone && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Phone size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.phone}</span>
                      </div>
                    )}
                    {selectedApp.contact_name && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Users size={14} className="text-[#D4AF37]" />
                        <span>{selectedApp.contact_name}</span>
                      </div>
                    )}
                    {(selectedApp.city || selectedApp.province) && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <MapPin size={14} className="text-[#D4AF37]" />
                        <span>{[selectedApp.city, selectedApp.province].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar size={14} className="text-[#D4AF37]" />
                      <span>{new Date(selectedApp.created_at).toLocaleString('es-AR')}</span>
                    </div>
                    {selectedApp.website && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Globe size={14} className="text-[#D4AF37]" />
                        <a href={selectedApp.website} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline truncate">
                          {selectedApp.website}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Stats */}
                  <div className="flex gap-6 mt-4 text-sm">
                    {selectedApp.expected_events_per_month && (
                      <span className="text-zinc-500">
                        Eventos/mes: <span className="text-white">{selectedApp.expected_events_per_month}</span>
                      </span>
                    )}
                    {selectedApp.average_event_capacity && (
                      <span className="text-zinc-500">
                        Capacidad promedio: <span className="text-white">{selectedApp.average_event_capacity}</span>
                      </span>
                    )}
                  </div>
                  
                  {selectedApp.description && (
                    <div className="mt-4 p-3 bg-zinc-900 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-1">Descripción:</p>
                      <p className="text-zinc-300 text-sm">{selectedApp.description}</p>
                    </div>
                  )}
                  
                  {selectedApp.message && (
                    <div className="mt-4 p-3 bg-zinc-900 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-1">Mensaje adicional:</p>
                      <p className="text-zinc-300 text-sm italic">"{selectedApp.message}"</p>
                    </div>
                  )}
                  
                  {/* Social Links */}
                  {selectedApp.social_links?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-zinc-500 mb-2">Redes Sociales:</p>
                      <div className="flex gap-2">
                        {selectedApp.social_links.map((link, i) => (
                          <a 
                            key={i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
                          >
                            {link.platform === 'instagram' && <Instagram size={16} />}
                            {link.platform === 'twitter' && <Twitter size={16} />}
                            {link.platform === 'youtube' && <Youtube size={16} />}
                            {link.platform === 'spotify' && <Music size={16} />}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Responses */}
                {selectedApp.form_responses?.length > 0 && (
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4">
                      Respuestas del Formulario
                    </h4>
                    <div className="space-y-4">
                      {selectedApp.form_responses.map((response, index) => (
                        <div key={index} className="border-b border-zinc-700 pb-3 last:border-0 last:pb-0">
                          <p className="text-xs text-zinc-500 mb-1">{response.field_label || response.field_id}</p>
                          <p className="text-white text-sm whitespace-pre-wrap">
                            {response.field_type === 'checkbox' 
                              ? (response.value ? '✓ Sí' : '✗ No')
                              : (response.value || '-')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {selectedApp.status === 'pending' && (
                  <div className="flex gap-3 pt-4 border-t border-zinc-700">
                    <Button
                      onClick={() => {
                        openRejectModal(selectedApp);
                        setShowApplicationDetail(false);
                      }}
                      className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                    >
                      <X size={16} className="mr-2" />
                      Rechazar
                    </Button>
                    <Button
                      onClick={() => {
                        openApproveModal(selectedApp);
                        setShowApplicationDetail(false);
                      }}
                      className="flex-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                    >
                      <Check size={16} className="mr-2" />
                      Aprobar
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Grant Subscription Modal */}
        <Dialog open={showGrantSubModal} onOpenChange={setShowGrantSubModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Crown className="text-[#D4AF37]" size={20} />
                Otorgar Super Productora
              </DialogTitle>
            </DialogHeader>
            
            {grantSubProducer && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <Building2 className="text-zinc-500" size={20} />
                  <div>
                    <p className="font-medium text-white">{grantSubProducer.business_name}</p>
                    <p className="text-sm text-zinc-500">{grantSubProducer.email}</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Fecha de Expiración (opcional)</Label>
                  <Input
                    type="date"
                    value={grantSubData.expires_at}
                    onChange={(e) => setGrantSubData({ ...grantSubData, expires_at: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Deja vacío para suscripción indefinida
                  </p>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Notas internas</Label>
                  <Textarea
                    value={grantSubData.notes}
                    onChange={(e) => setGrantSubData({ ...grantSubData, notes: e.target.value })}
                    placeholder="Ej: Suscripción de cortesía por..."
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={2}
                  />
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowGrantSubModal(false);
                  setGrantSubProducer(null);
                  setGrantSubData({ expires_at: '', notes: '' });
                }}
                className="flex-1 border-zinc-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleGrantSubscription}
                disabled={savingSettings}
                className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black"
              >
                {savingSettings ? <Loader2 className="animate-spin mr-2" size={16} /> : <Crown size={16} className="mr-2" />}
                Otorgar Suscripción
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default ProducersAdminPage;
